import type { Post } from "./types";
import heroLendoBillan from "@/assets/news/lendo-billan.jpg";

export const POSTS_19: Record<string, Post> = {
  "lendo-billan": {
    title: "Lendo billån – så fungerar det och vad det kostar 2026",
    intro:
      "Har du hittat bilen men inte finansieringen, och känner att bilhandlarens erbjudande låter dyrt? Vi förstår – ett billån är oftast det näst största lånet man tar i livet och några räntepunkter blir tusenlappar. Här går vi igenom hur Lendo billån fungerar 2026, vad det faktiskt kostar per månad, vilka krav som gäller, hur Lendo skiljer sig från handlarens finansiering och när något annat är billigare.",
    author: "Erik Lindqvist",
    authorTitle: "finansiell skribent, 9 år inom konsumentkrediter",
    published: "9 september 2026",
    updated: "9 september 2026",
    heroSrc: heroLendoBillan,
    heroAlt: "Par tar emot bilnycklar efter att ha jämfört Lendo billån hos bilhandlaren",
    topLenderId: "lendo",
    bottomLenderId: "sambla",
    topLenderTitle: "Populärast för billån – jämför flera banker i en ansökan",
    bottomLenderTitle: "Vår rekommendation för att pressa bilräntan ytterligare",
    reviewsLenderId: "sambla",
    cta: { to: "/billan", label: "Jämför billån från flera banker" },
    calculator: {
      title: "Räkna på Lendo billån",
      intro:
        "Dra i reglagen och se månadskostnad, räntekostnad, total lånekostnad och effektiv ränta för ditt billån innan du signerar hos handlaren.",
      note:
        "Månadskostnaden på ett billån styrs av lånebelopp, löptid och nominell ränta, men det är den effektiva räntan som visar din verkliga kostnad eftersom uppläggningsavgift och aviavgift räknas in. En löptid på tio år ger låg månadskostnad men klart högre total lånekostnad än fem år. Testa flera löptider innan du väljer.",
      amount: 200000,
      minAmount: 10000,
      maxAmount: 600000,
      months: 84,
      maxMonths: 144,
      rate: 7.45,
      minRate: 3,
      maxRate: 20,
      setupFee: 495,
      invoiceFee: 29,
    },
    faq: [
      {
        q: "Vad är Lendo billån?",
        a: "Lendo är en låneförmedlare, inte en bank. Du fyller i en ansökan och Lendo skickar den vidare till anslutna banker som lämnar varsitt erbjudande. Du väljer själv vilket du tackar ja till, och du kan tacka nej till alla utan kostnad.",
      },
      {
        q: "Hur många UC-förfrågningar blir det hos Lendo?",
        a: "En enda. Lendo tar upplysningen och delar den med bankerna, så din kreditvärdighet påverkas som vid en ansökan – inte som vid tio. Det är den största praktiska fördelen mot att söka hos varje bank för sig.",
      },
      {
        q: "Vad kostar Lendo billån?",
        a: "Själva tjänsten är gratis för dig; Lendo får provision från banken du väljer. Kostnaden ligger i lånet: ränta, uppläggningsavgift på runt 400–700 kr och aviavgift på cirka 29 kr per månad.",
      },
      {
        q: "Vilken ränta får man på billån 2026?",
        a: "Ett billån med bilen som säkerhet ligger 2026 typiskt mellan 5 och 9 procent. Ett billån utan säkerhet, alltså i praktiken ett privatlån, hamnar oftare mellan 7 och 14 procent beroende på din ekonomi.",
      },
      {
        q: "Vilka krav ställer Lendo för billån?",
        a: "Du behöver vara minst 18 år, folkbokförd i Sverige, ha en deklarerad inkomst och sakna aktiv betalningsanmärkning hos de flesta anslutna banker. Enskilda banker har egna, ofta högre, inkomstkrav.",
      },
      {
        q: "Hur lång tid tar det att få svar?",
        a: "De första erbjudandena kommer ofta inom några minuter och resten under samma arbetsdag. Efter att du signerat med BankID betalas pengarna normalt ut inom en till tre bankdagar.",
      },
      {
        q: "Är Lendo billån bättre än bilhandlarens finansiering?",
        a: "Ofta ja när det gäller räntan, eftersom du får flera bud att jämföra. Handlarens kampanjränta kan däremot vara mycket låg på nya bilar – räkna alltid på totalkostnaden och inte bara på månadsbeloppet.",
      },
    ],
    blocks: [
      { t: "h2", text: "Lendo billån – kort svar" },
      { t: "p", text: "Lendo billån är ingen egen bank utan en jämförelsetjänst. Du gör en ansökan, Lendo tar en enda kreditupplysning och skickar den till anslutna banker, och du väljer sedan det billigaste erbjudandet. Tjänsten är gratis och du är inte bunden av något förrän du signerar." },
      { t: "p", text: "Vi vet att bilaffären ofta gör sig påmind samma dag som du står i hallen. Just därför är det värt en halvtimme att jämföra – räntespannet mellan bästa och sämsta bud brukar vara flera procentenheter." },
      {
        t: "stats",
        items: [
          { label: "Antal UC-förfrågningar", value: "1 st" },
          { label: "Typisk billåneränta 2026", value: "5–9 %" },
          { label: "Kostnad för tjänsten", value: "0 kr" },
        ],
      },

      { t: "h2", text: "Så fungerar Lendo billån steg för steg" },
      { t: "p", text: "Processen är densamma oavsett om bilen köps privat eller hos handlare. Skillnaden ligger i vilken säkerhet banken accepterar." },
      {
        t: "steps",
        title: "Från ansökan till utbetalning",
        items: [
          "Fyll i belopp, löptid och uppgifter om din ekonomi.",
          "Signera ansökan med BankID – en kreditupplysning tas.",
          "Ta emot erbjudanden från anslutna banker, ofta inom minuter.",
          "Jämför effektiv ränta, inte bara månadskostnaden.",
          "Tacka ja till ett bud och signera avtalet med BankID.",
          "Pengarna betalas ut, normalt inom en till tre bankdagar.",
        ],
      },
      { t: "p", text: "Behöver du gå igenom grunderna först rekommenderar vi vår [låneguide för nybörjaren](/nyhet/laneguide-for-nyborjaren) innan du fyller i ansökan." },

      { t: "h2", text: "Vad kostar ett billån via Lendo i kronor?" },
      { t: "p", text: "Räntesatsen säger lite i sig. Det som avgör är hur mycket du betalar tillbaka totalt när avgifterna räknats in." },
      {
        t: "table",
        caption: "Räkneexempel: billån 200 000 kr, 7,45 % ränta, 495 kr uppläggning, 29 kr avi",
        head: ["Löptid", "Månadskostnad", "Total kostnad", "Varav ränta och avgifter"],
        rows: [
          ["5 år (60 mån)", "cirka 4 030 kr", "cirka 241 800 kr", "cirka 41 800 kr"],
          ["7 år (84 mån)", "cirka 3 090 kr", "cirka 259 600 kr", "cirka 59 600 kr"],
          ["10 år (120 mån)", "cirka 2 400 kr", "cirka 288 000 kr", "cirka 88 000 kr"],
        ],
      },
      { t: "p", text: "**Skillnaden mellan fem och tio års löptid är runt 46 000 kr** på samma lån. Månadskostnaden lockar, men det är totalen du faktiskt betalar." },

      { t: "h2", text: "Billån med säkerhet eller privatlån utan säkerhet?" },
      { t: "h3", text: "Billån med bilen som säkerhet" },
      { t: "p", text: "Lägre ränta eftersom banken kan återta bilen. Kräver oftast 20 procent kontantinsats och att bilen inte är för gammal – många banker sätter gränsen vid 8–10 år." },
      { t: "h3", text: "Privatlån utan säkerhet" },
      { t: "p", text: "Högre ränta men full frihet: du äger bilen direkt, kan köpa privat och slipper krav på kontantinsats. Passar bra för begagnat i lägre prisklass." },
      {
        t: "proscons",
        title: "Lendo billån – för- och nackdelar",
        pros: [
          "Flera bankerbjudanden på en enda kreditupplysning.",
          "Kostnadsfritt och helt utan förpliktelser.",
          "Enkelt att jämföra ränta och löptid sida vid sida.",
          "Fungerar både för handlarköp och privatköp.",
        ],
        cons: [
          "Lendo är ingen bank – villkoren sätts av respektive långivare.",
          "Kräver UC, så det passar dåligt med många färska förfrågningar.",
          "Betalningsanmärkning ger avslag hos de flesta anslutna banker.",
          "Erbjudandena kan skilja sig mycket från den ränta som marknadsförs.",
        ],
      },

      { t: "h2", text: "Krav för att bli beviljad" },
      {
        t: "table",
        caption: "Vanliga krav hos Lendos anslutna banker 2026",
        head: ["Krav", "Vad som gäller"],
        rows: [
          ["Ålder", "Minst 18 år, flera banker kräver 20 år"],
          ["Inkomst", "Deklarerad inkomst, ofta minst 120 000–180 000 kr per år"],
          ["Betalningsanmärkning", "Ger avslag hos majoriteten av bankerna"],
          ["Kronofogden", "Aktivt mål ger i praktiken alltid avslag"],
          ["Bilens ålder", "Vid säkerhet vanligtvis max 8–10 år"],
        ],
      },
      { t: "p", text: "Har du redan flera avslag bakom dig kan det vara värt att läsa vår guide om [låna pengar utan avslag](/nyhet/lana-pengar-utan-avslag) innan du ansöker igen." },

      { t: "h2", text: "Lendo billån jämfört med handlarens finansiering" },
      { t: "p", text: "Bilhandlaren tjänar på finansieringen, precis som på bilen. Kampanjräntor på nya bilar kan ändå vara svårslagna eftersom tillverkaren subventionerar dem." },
      {
        t: "box",
        tone: "tip",
        title: "Så gör du jämförelsen rättvis",
        items: [
          "Be alltid om effektiv ränta, inte nominell.",
          "Räkna på totalkostnaden över hela löptiden.",
          "Kontrollera om restvärde eller ballongbetalning ingår.",
          "Fråga vad kontantinsatsen är och om den kan sänkas.",
          "Ta med Lendos bästa bud till handlaren – det pressar priset.",
        ],
      },

      { t: "h2", text: "Så snabbt får du pengarna" },
      { t: "p", text: "Erbjudanden kommer inom minuter, men utbetalningen tar normalt en till tre bankdagar eftersom avtalet ska registreras. Köper du hos handlare betalas beloppet ofta ut direkt till bilfirman." },
      { t: "p", text: "Behöver du pengar samma dag är billån fel produkt. Läs i så fall om [snabblån med utbetalning direkt](/nyhet/snabblan-utbetalning-direkt) – men var medveten om att räntan blir väsentligt högre." },

      { t: "h2", text: "Ränteläget 2026 och vad det betyder för bilköpet" },
      { t: "p", text: "Efter Riksbankens sänkningar under 2025 ligger styrräntan stilla i början av 2026, och billåneräntorna har följt med nedåt. Har du ett billån tecknat 2023 eller 2024 är det ofta lönsamt att lösa in det." },
      { t: "p", text: "Kontrollera villkoren först. På ett rörligt lån får banken enligt konsumentkreditlagen inte ta ut någon ränteskillnadsersättning när du löser lånet i förtid." },

      { t: "h2", text: "Vanliga misstag vid billån" },
      {
        t: "ul",
        items: [
          "Att bara jämföra månadskostnad i stället för totalkostnad.",
          "Att välja tio års löptid på en bil som håller i fem.",
          "Att glömma räkna in försäkring, skatt och service i kalkylen.",
          "Att signera hos handlaren innan något annat bud hämtats in.",
          "Att missa restvärdet i ett leasingliknande upplägg.",
        ],
      },

      { t: "h2", text: "Hur säkert är Lendo?" },
      { t: "p", text: "Lendo står under Finansinspektionens tillsyn som kreditförmedlare och är en av Sveriges största aktörer. Uppgifterna delas bara med de banker som prövar din ansökan." },
      { t: "p", text: "Kontrollera alltid en aktör i [Finansinspektionens företagsregister](https://www.fi.se/sv/vara-register/foretagsregistret/) innan du lämnar personuppgifter. Fristående råd finns hos [Hallå konsument](https://www.hallakonsument.se/) och [Konsumentverket](https://www.konsumentverket.se/)." },

      { t: "h2", text: "Alternativ till Lendo för billån" },
      { t: "p", text: "Flera förmedlare arbetar på samma sätt men har olika bankpaneler. Att prova två förmedlare med några veckors mellanrum ger ofta ett bättre bud än att nöja sig med det första." },
      { t: "p", text: "Läs också vår genomgång av [billån utan kontantinsats](/nyhet/billan-utan-kontantinsats) och jämförelsen [låna pengar till bilköp](/nyhet/lana-pengar-till-bilkop)." },

      { t: "h2", text: "Så granskar vi långivarna" },
      { t: "p", text: "Vi väger ränta, avgifter, utbetalningstid, villkorens tydlighet, tillstånd hos Finansinspektionen och kundomdömen. Ingen aktör kan köpa en placering. Läs mer om [hur vi granskar](/om-oss/hur-vi-granskar) och om vår [redaktion](/om-oss/redaktion)." },

      { t: "h2", text: "Sammanfattning" },
      { t: "p", text: "Lendo billån ger dig flera bankbud på en enda kreditupplysning, kostnadsfritt och utan bindning. Det är ett av de enklaste sätten att pressa bilräntan, men villkoren sätts av banken – inte av Lendo. Jämför effektiv ränta, välj kortast löptid du klarar av och ta med bästa budet till handlaren innan du signerar." },

      { t: "h2", text: "Källor" },
      {
        t: "ul",
        items: [
          "Finansinspektionen – företagsregister och tillsyn över kreditförmedlare.",
          "Konsumentverket och Hallå konsument – konsumentkreditlagen, ångerrätt och förtidsinlösen.",
          "Riksbanken – styrränta och ränteläge 2026.",
        ],
      },
    ],
  },
};
